import {
  TableHeaderCellScopeType,
  type RegionTableDataFragment,
} from "../client/generated";
import type {
  CellIdentifier,
  CellState,
  ColState,
  RowState,
  TableState,
} from "./types";
import { CSS_IN_TO_PX } from "../editor/utils";
import { assertTypeName } from "../client/guards";

export const encodeCellIdentifier = (cell: CellIdentifier): string => {
  return `${cell.originRow}-${cell.originCol}`;
};

export const decodeCellIdentifier = (value: string): CellIdentifier => {
  const [originRow, originCol] = value.split("-").map((n) => parseInt(n, 10));
  return { originRow, originCol };
};

export function getTableFromRegion(
  fragment: RegionTableDataFragment,
): TableState | undefined {
  const tableAttribute = fragment.attributes.find(
    (attr) => attr.__typename === "TableAttribute",
  );

  const tableWidth = fragment.shape.aabb.width;
  const tableHeight = fragment.shape.aabb.height;
  const tableTop = fragment.shape.aabb.top;
  const tableLeft = fragment.shape.aabb.left;
  const tableRight = tableLeft + tableWidth;
  const tableBottom = tableTop + tableHeight;
  const tableTopInPx = tableTop * fragment.page.height * CSS_IN_TO_PX;
  const tableLeftInPx = tableLeft * fragment.page.width * CSS_IN_TO_PX;

  if (tableAttribute?.__typename !== "TableAttribute") {
    // Default to a single-cell-table, if the region has no table attribute yet.
    return {
      position: {
        x: tableLeftInPx,
        y: tableTopInPx,
      },
      rows: [
        {
          index: 0,
          height: tableHeight * fragment.page.height * CSS_IN_TO_PX,
          group: "tbody",
        },
      ],
      cols: [
        {
          index: 0,
          width: tableWidth * fragment.page.width * CSS_IN_TO_PX,
        },
      ],
      cells: [
        {
          originRow: 0,
          originCol: 0,
          colSpan: 1,
          rowSpan: 1,
          role: "td",
        },
      ],
    };
  }

  const rows: RowState[] = tableAttribute.rows.map((height, index) => ({
    index,
    height: height * (tableHeight * fragment.page.height * CSS_IN_TO_PX),
    group: "tbody",
  }));

  const cols: ColState[] = tableAttribute.columns.map((width, index) => ({
    index,
    width: width * (tableWidth * fragment.page.width * CSS_IN_TO_PX),
  }));

  const tableCellRegions = fragment.page.regions.filter((region) => {
    const regionTop = region.shape.aabb.top;
    const regionLeft = region.shape.aabb.left;
    const regionRight = region.shape.aabb.left + region.shape.aabb.width;
    const regionBottom = region.shape.aabb.top + region.shape.aabb.height;

    return (
      region.attributes.some(
        (attr) => attr.__typename === "TableCellAttribute",
      ) &&
      regionTop >= tableTop &&
      regionLeft >= tableLeft &&
      regionRight <= tableRight &&
      regionBottom <= tableBottom
    );
  });

  console.log("TABLE CELL REGIONS", tableCellRegions);

  const cells: CellState[] = tableCellRegions.map((region) => {
    const tableCellAttr = region.attributes.find(
      (attr) => attr.__typename === "TableCellAttribute",
    );
    assertTypeName(tableCellAttr?.__typename === "TableCellAttribute");

    const tableHeaderCellAttr = region.attributes.find(
      (attr) => attr.__typename === "TableHeaderCellAttribute",
    );
    assertTypeName(
      tableHeaderCellAttr?.__typename === "TableHeaderCellAttribute" ||
        tableHeaderCellAttr === undefined,
    );

    const hasColScope = tableHeaderCellAttr?.scopes.some(
      (scope) => scope.type === TableHeaderCellScopeType.Column,
    );
    const hasRowScope = tableHeaderCellAttr?.scopes.some(
      (scope) => scope.type === TableHeaderCellScopeType.Row,
    );
    const scope = hasColScope ? "col" : hasRowScope ? "row" : undefined;

    const headers = tableCellRegions.reduce((headers, otherRegion) => {
      const tableHeaderCellAttr = otherRegion.attributes.find(
        (attr) => attr.__typename === "TableHeaderCellAttribute",
      );

      if (!tableHeaderCellAttr) return headers;

      assertTypeName(
        tableHeaderCellAttr.__typename === "TableHeaderCellAttribute",
      );

      const isHeaderOfThisCell = tableHeaderCellAttr.scopes.some(
        (scope) => scope.link === region.id,
      );

      if (!isHeaderOfThisCell) return headers;

      const otherRegionTopInPx =
        otherRegion.shape.aabb.top * fragment.page.height * CSS_IN_TO_PX;
      const otherRegionLeftInPx =
        otherRegion.shape.aabb.left * fragment.page.width * CSS_IN_TO_PX;

      const otherRegionOriginRow = getOriginRowFromPosition(
        rows,
        otherRegionTopInPx - tableTopInPx,
      );
      const otherRegionOriginCol = getOriginColFromPosition(
        cols,
        otherRegionLeftInPx - tableLeftInPx,
      );

      return [
        ...headers,
        {
          originRow: otherRegionOriginRow,
          originCol: otherRegionOriginCol,
        },
      ];
    }, [] as CellIdentifier[]);

    const topInPx = region.shape.aabb.top * fragment.page.height * CSS_IN_TO_PX;
    const leftInPx =
      region.shape.aabb.left * fragment.page.width * CSS_IN_TO_PX;

    return {
      originRow: getOriginRowFromPosition(rows, topInPx - tableTopInPx),
      originCol: getOriginColFromPosition(cols, leftInPx - tableLeftInPx),
      colSpan: tableCellAttr.columnSpan,
      rowSpan: tableCellAttr.rowSpan,
      role: tableHeaderCellAttr ? "th" : "td",
      scope,
      headers,
    };
  });

  return {
    position: {
      x: tableLeftInPx,
      y: tableTopInPx,
    },
    rows,
    cols,
    cells,
  };
}

function getOriginColFromPosition(cols: ColState[], left: number): number {
  let originCol = 0;
  let width = 0;

  for (const col of cols) {
    width += col.width;

    if (left + LEEWAY_IN_PX < width) {
      break;
    }

    originCol++;
  }

  return originCol;
}

function getOriginRowFromPosition(rows: RowState[], top: number): number {
  let originRow = 0;
  let height = 0;

  for (const row of rows) {
    height += row.height;

    if (top + LEEWAY_IN_PX < height) {
      break;
    }

    originRow++;
  }

  return originRow;
}

const LEEWAY_IN_PX = 0.01;
